import { ValidationAssetType } from "@/api/validation-types";
import {
  IMAGE_REJECTION_COUNT_BEFORE_MANUAL_EDIT,
  MAX_NOTES_LENGTH,
} from "@/const";
import { useImageById } from "@/queries/images/use-image-by-id";
import { rejectionReasons } from "@/utils/rejection-reasons";
import { Close } from "@mui/icons-material";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";

type Props = {
  asset:
    | {
        assetId: string;
        type: ValidationAssetType;
      }
    | undefined;
  open: boolean;
  onClose: () => void;
  onReject: (reasons: string[], notes?: string) => void;
};

export function RejectionDialog({ asset, open, onClose, onReject }: Props) {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [customReason, setCustomReason] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();

  const reasons = asset ? rejectionReasons[asset?.type] : [];

  const { data: imageDetails } = useImageById(
    asset?.type === ValidationAssetType.image ? asset.assetId : undefined,
  );

  const rejectionLimitReached = useMemo(() => {
    return (
      imageDetails &&
      imageDetails.previousImagesCount >=
        IMAGE_REJECTION_COUNT_BEFORE_MANUAL_EDIT - 1
    );
  }, [imageDetails]);

  const handleReject = () => {
    if (rejectionLimitReached && !notes) {
      setError("Please provide notes for rejection.");
      return;
    }
    const allReasons = [...selectedReasons, customReason].filter(
      (reason) => reason !== "",
    );
    onReject(allReasons, notes);
    setSelectedReasons([]);
    setCustomReason("");
    setNotes("");
    setError(null);
  };

  const handleClose = () => {
    onClose();
    setSelectedReasons([]);
    setCustomReason("");
    setNotes("");
    setError(null);
  };

  return (
    <Dialog onClose={handleClose} fullWidth maxWidth="md" open={open}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Give rejection reason
        <Button onClick={handleClose} sx={{ minWidth: 0 }}>
          <Close sx={{ color: theme.palette.common.black }} />
        </Button>
      </DialogTitle>
      <DialogContent>
        {rejectionLimitReached && (
          <Alert sx={{ mb: 2 }} severity="warning">
            {`Note: This is the ${IMAGE_REJECTION_COUNT_BEFORE_MANUAL_EDIT}${
              { 1: "st", 2: "nd", 3: "rd" }[
                IMAGE_REJECTION_COUNT_BEFORE_MANUAL_EDIT
              ] ?? "th"
            } rejection for this image. It will now be sent for manual correction. Please use the text box to clearly describe the required changes.`}
          </Alert>
        )}
        <FormGroup row sx={{ mb: 2 }}>
          {reasons.map((reason, idx) => (
            <Tooltip
              key={reason.title}
              title={reason.description}
              disableInteractive
              arrow
            >
              <FormControlLabel
                label={reason.title}
                control={
                  <Checkbox
                    autoFocus={idx === 0}
                    checked={selectedReasons.includes(reason.title)}
                    onChange={(e) =>
                      e.target.checked
                        ? setSelectedReasons([...selectedReasons, reason.title])
                        : setSelectedReasons(
                            selectedReasons.filter((r) => r !== reason.title),
                          )
                    }
                  />
                }
              />
            </Tooltip>
          ))}
        </FormGroup>
        <TextField
          placeholder="Give other reason"
          label={"Other"}
          fullWidth
          value={customReason}
          onChange={(event) => {
            setCustomReason(event.target.value);
          }}
          sx={{ mb: 2 }}
        />
        <TextField
          placeholder="Add any additional notes"
          label={"Notes"}
          fullWidth
          multiline
          rows={3}
          value={notes}
          onChange={(event) => {
            setError(null);
            setNotes(event.target.value);
          }}
          inputProps={{ maxLength: MAX_NOTES_LENGTH }}
          error={!!error}
          helperText={error || `${notes.length}/${MAX_NOTES_LENGTH} characters`}
        />
      </DialogContent>
      <DialogActions sx={{ padding: 3, paddingTop: 0, paddingBottom: 2 }}>
        <Button onClick={handleReject}>Reject</Button>
      </DialogActions>
    </Dialog>
  );
}
